import React, { useMemo } from 'react'
import { FlatList, StyleSheet } from 'react-native'

import EmptyListPlaceholder from './components/EmptyListPlaceholder'
import SelectContainer from './components/SelectContainer'
import TokenNameFilterChips from './components/TokenNameFilterChips'
import { SectionedSelectProps, SelectProps } from './types'
import useSelectInternal from './useSelectInternal'

const Select = ({
  setValue,
  value,
  options,
  testID,
  menuOptionHeight,
  attemptToFetchMoreOptions,
  emptyListPlaceholderText,
  onSearch,
  withNameFilterChips,
  ...props
}: SelectProps) => {
  const selectData = useSelectInternal({
    value,
    setValue,
    // To address the structural differences between SectionList and FlatList,
    // we wrap non-sectioned list data in a default single section
    data: [{ data: options, title: '', key: 'default' }] as SectionedSelectProps['sections'],
    menuOptionHeight,
    attemptToFetchMoreOptions,
    mode: props.mode,
    onSearch,
    withNameFilterChips
  })
  const {
    listRef,
    filteredData,
    renderItem,
    keyExtractor,
    getItemLayout,
    handleLayout,
    handleScroll,
    nameFilterChips,
    control,
    setSearch
  } = selectData

  // Chips live in the list header so they scroll with the rows. `nameFilterChips`
  // is a NEW array on every render (it's derived from `data`, which Select rebuilds
  // each render), so keying the memo on the array identity would hand FlatList a
  // fresh `ListHeaderComponent` on every render and remount the whole chip subtree.
  // That remount is what caused the "must tap a chip twice" bug: tapping a chip
  // blurs the auto-focused search input, that blur triggers a re-render, and the
  // chip DOM node the press started on is torn down before the mouseup lands — so
  // no `click` fires and the first tap is swallowed. Key the memo on the chip
  // *content* instead so the header stays mounted across such incidental re-renders.
  const nameFilterChipsKey = nameFilterChips.join('|')
  const ListHeaderComponent = useMemo(() => {
    if (!nameFilterChips.length) return undefined
    return () => (
      <TokenNameFilterChips chips={nameFilterChips} control={control} setSearch={setSearch} />
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilterChipsKey, control, setSearch])

  return (
    <SelectContainer
      value={value}
      {...selectData}
      {...props}
      menuProps={{ ...selectData.menuProps, ...(props.menuProps || {}) }}
      id={testID}
      testID={testID}
    >
      <FlatList
        ref={listRef}
        // In bottomSheet mode fill the fixed-height sheet body and scroll
        // internally when the list is longer than the available space. StyleSheet
        // (not inline) is required for react-native-web to bound the list height.
        // Left unset in dropdown ('select') mode, whose MenuContainer is
        // auto-height and would collapse under `flex: 1`.
        style={props.mode === 'bottomSheet' ? selectListStyles.list : undefined}
        // get the data (the options) from the default section
        data={filteredData?.[0]?.data || []}
        renderItem={renderItem as any}
        ListHeaderComponent={ListHeaderComponent}
        keyExtractor={keyExtractor}
        onLayout={handleLayout}
        initialNumToRender={15}
        windowSize={10}
        maxToRenderPerBatch={20}
        removeClippedSubviews
        getItemLayout={getItemLayout as any}
        ListEmptyComponent={<EmptyListPlaceholder placeholderText={emptyListPlaceholderText} />}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      />
    </SelectContainer>
  )
}

const selectListStyles = StyleSheet.create({
  list: {
    flex: 1
  }
})

export default React.memo(Select)
